import React, { useState } from 'react';
import { ArrowLeft, ChevronRight, LayoutGrid, Table } from 'lucide-react';
import { useObject } from '../../services/queries/objectQueries';
import { useFieldsByObject, type CustomField } from '../../services/queries/fieldQueries';
import { FieldsTab } from './object-detail/FieldsTab';
import { PageLayoutsTab } from './object-detail/PageLayoutsTab';
import { RecordPagesTab } from './object-detail/RecordPagesTab';
import { RelationshipsTab } from './object-detail/RelationshipsTab';
import { TabConfigHome } from './object-manager/TabConfigHome';
import { ListViewsHome } from './object-manager/ListViewsHome';
import { ValidationRulesHome } from './object-manager/ValidationRulesHome';
import { ButtonsActionsHome } from './object-manager/ButtonsActionsHome';
import { GenericObjectListView } from '../lists/GenericObjectListView';
import {
  StandardButton,
  StandardLoadingState,
  StandardErrorState
} from '../standard';

interface ObjectDetailProps {
  objectId: string;
  onBack: () => void;
  onNavigateToPageLayouts?: (layoutId?: string, objectName?: string) => void;
  onNavigateToTabConfig?: (configId?: string, objectName?: string) => void;
}

export const ObjectDetail: React.FC<ObjectDetailProps> = ({
  objectId,
  onBack,
  onNavigateToPageLayouts,
  onNavigateToTabConfig
}) => {
  const [activeTab, setActiveTab] = useState('fields');

  const { data: object, isLoading, error } = useObject(objectId);
  const { data: fields } = useFieldsByObject(objectId);

  const fieldsForBuilders = React.useMemo(() => {
    if (!fields) return undefined;
    return fields.map((f: CustomField) => ({
      ...f,
      label: f.fieldLabel,
      type: f.fieldType,
      options: f.picklistValues?.map(v => ({ value: v, label: v }))
    }));
  }, [fields]);

  if (isLoading) {
    return <StandardLoadingState message="Loading object details..." />;
  }

  if (error || !object) {
    return (
      <div className="p-6">
        <StandardErrorState
          title="Error loading object"
          message="Please try again."
        />
        <div className="mt-4 flex justify-center">
          <StandardButton
            variant="secondary"
            onClick={onBack}
            leftIcon={<ArrowLeft className="w-4 h-4" />}
          >
            Back to Object Manager
          </StandardButton>
        </div>
      </div>
    );
  }

  return (
    <div className="object-detail h-full flex flex-col bg-gray-50 dark:bg-gray-900">
      {/* Breadcrumb & Header */}
      <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800">
        <div className="flex items-center mb-3">
          <button
            onClick={onBack}
            className="mr-3 p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded transition-colors focus:outline-none focus:ring-2 focus:ring-purple-500"
            title="Back to Object Manager"
          >
            <ArrowLeft className="h-5 w-5 text-gray-600 dark:text-gray-400" />
          </button>
          <div className="flex items-center text-sm text-gray-500 dark:text-gray-400">
            <button
              onClick={onBack}
              className="hover:text-gray-700 dark:hover:text-gray-300 focus:outline-none focus:underline"
            >
              Object Manager
            </button>
            <ChevronRight className="h-4 w-4 mx-2" />
            <span className="font-semibold text-gray-900 dark:text-gray-100">{object.label}</span>
          </div>
        </div>

        <div className="flex items-start justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-1">{object.label}</h1>
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">{object.apiName}</p>
            <p className="text-sm text-gray-700 dark:text-gray-300">{object.description}</p>
          </div>
          <div className="flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400">
            <span className="px-2 py-1 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded">
              {object.fieldCount} fields
            </span>
            <span className="px-2 py-1 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded">
              {object.recordCount} records
            </span>
          </div>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="border-b border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 px-6">
        <nav className="flex space-x-8 -mb-px">
          <TabButton
            label="Fields"
            active={activeTab === 'fields'}
            onClick={() => setActiveTab('fields')}
          />
          <TabButton
            label="Records"
            active={activeTab === 'records'}
            onClick={() => setActiveTab('records')}
          />
          <TabButton
            label="Relationships"
            active={activeTab === 'relationships'}
            onClick={() => setActiveTab('relationships')}
          />
          <TabButton
            label="Page Layouts"
            active={activeTab === 'layouts'}
            onClick={() => setActiveTab('layouts')}
          />
          <TabButton
            label="Tab Configuration"
            active={activeTab === 'tab-config'}
            onClick={() => setActiveTab('tab-config')}
          />
          <TabButton
            label="List Views"
            active={activeTab === 'list-views'}
            onClick={() => setActiveTab('list-views')}
          />
          <TabButton
            label="Record Pages"
            active={activeTab === 'record-pages'}
            onClick={() => setActiveTab('record-pages')}
          />
          <TabButton
            label="Validation Rules"
            active={activeTab === 'validation'}
            onClick={() => setActiveTab('validation')}
          />
          <TabButton
            label="Buttons & Actions"
            active={activeTab === 'actions'}
            onClick={() => setActiveTab('actions')}
          />
        </nav>
      </div>

      {/* Tab Content */}
      <div className="flex-1 overflow-y-auto bg-gray-50 dark:bg-gray-900">
        {activeTab === 'fields' && <FieldsTab objectId={objectId} object={object} />}
        {activeTab === 'records' && (
          <div className="p-6">
            <GenericObjectListView
              objectApiName={object.apiName}
              objectId={objectId}
              objectLabel={object.label}
            />
          </div>
        )}
        {activeTab === 'relationships' && <RelationshipsTab objectId={objectId} object={object} />}
        {activeTab === 'layouts' && (
          <PageLayoutsTab
            objectId={objectId}
            object={object}
            onNavigateToUIConfig={() => onNavigateToPageLayouts?.()}
            onEditLayout={(layoutId) => onNavigateToPageLayouts?.(layoutId, object.label)}
          />
        )}
        {activeTab === 'tab-config' && (
          <TabConfigHome
            objectName={object.apiName}
            objectLabel={object.label}
          />
        )}
        {activeTab === 'list-views' && fields && (
          <ListViewsHome
            objectName={object.apiName}
            objectLabel={object.label}
            allFields={fields}
          />
        )}
        {activeTab === 'record-pages' && (
          <RecordPagesTab
            objectId={objectId}
            object={object}
          />
        )}
        {activeTab === 'validation' && fieldsForBuilders && (
          <ValidationRulesHome
            objectName={object.apiName}
            objectLabel={object.label}
            allFields={fieldsForBuilders}
          />
        )}
        {activeTab === 'actions' && fieldsForBuilders && (
          <ButtonsActionsHome
            objectName={object.apiName}
            objectLabel={object.label}
            allFields={fieldsForBuilders}
          />
        )}
      </div>
    </div>
  );
};

// ============================================
// TAB BUTTON COMPONENT
// ============================================

interface TabButtonProps {
  label: string;
  active: boolean;
  onClick: () => void;
  badge?: string;
}

const TabButton: React.FC<TabButtonProps> = ({ label, active, onClick, badge }) => (
  <button
    onClick={onClick}
    className={`
      py-4 px-1 border-b-2 font-medium text-sm transition-colors relative focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2
      ${active
        ? 'border-purple-600 dark:border-purple-400 text-purple-600 dark:text-purple-400'
        : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:border-gray-300 dark:hover:border-gray-600'
      }
    `}
  >
    {label}
    {badge && (
      <span className="ml-2 px-2 py-0.5 bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-400 text-xs rounded">
        {badge}
      </span>
    )}
  </button>
);

// ============================================
// COMING SOON TAB PLACEHOLDER
// ============================================

interface ComingSoonTabProps {
  title: string;
  description: string;
}

const ComingSoonTab: React.FC<ComingSoonTabProps> = ({ title, description }) => (
  <div className="flex items-center justify-center h-full min-h-[400px] p-6">
    <div className="text-center max-w-md">
      <div className="mx-auto w-16 h-16 bg-purple-100 dark:bg-purple-900/30 rounded-full flex items-center justify-center mb-4">
        <ChevronRight className="w-8 h-8 text-purple-600 dark:text-purple-400" />
      </div>
      <h3 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-2">{title}</h3>
      <p className="text-gray-600 dark:text-gray-400 mb-4">{description}</p>
      <span className="inline-block px-4 py-2 bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-400 rounded-full text-sm font-medium">
        Coming Soon
      </span>
    </div>
  </div>
);

export default ObjectDetail;
